import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import GroupsIcon from '@mui/icons-material/Groups';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';

import CONFIG from '../../config.js';

const miniProgramQr = new URL('../assets/mini-program.jpg', import.meta.url);

const FEATURES = [
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
    title: '赛事报名',
    desc: '在线浏览赛事信息，一键报名参赛，支持个人和团队报名',
    color: '#C41E3A',
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    title: '在线教学',
    desc: '专业教练视频课程，智慧课堂实时直播，系统化学习象棋',
    color: '#B8860B',
  },
  {
    icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
    title: '人机对弈',
    desc: '多难度AI引擎，从入门到大师，随时随地磨炼棋艺',
    color: '#2E7D32',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    title: '棋友社区',
    desc: '活动相册、新闻资讯、赛事直播，连接每一位象棋爱好者',
    color: '#1565C0',
  },
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero 区域 */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2D1810 50%, #1A1A1A 100%)',
          color: '#fff',
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 装饰背景 */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: '5%',
            transform: 'translateY(-50%)',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(184,134,11,0.15) 0%, transparent 70%)',
            display: { xs: 'none', lg: 'block' },
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 650, position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                lineHeight: 1.15,
              }}
            >
              中象在线
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: 'linear-gradient(90deg, #D4A84B, #F0D68A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                专业的中国象棋学习平台
              </Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'grey.400',
                mb: 5,
                fontWeight: 400,
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              赛事报名、在线教学、人机对弈、棋友社区，一站式象棋学习与竞技平台。
              支持微信小程序和APP，随时随地享受象棋的乐趣。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PhoneIphoneIcon />}
                onClick={() => navigate('/download')}
                sx={{
                  bgcolor: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.05rem',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                下载应用
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/events')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.05rem',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                浏览赛事
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* 下载应用 */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 2 }}>
            立即体验中象在线
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 500, mx: 'auto' }}
          >
            扫描小程序码或下载APP，随时随地学棋、练棋、比赛
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 4,
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            {/* 微信小程序 */}
            <Card
              sx={{
                textAlign: 'center',
                p: 4,
                border: '2px solid #07C160',
                borderRadius: 3,
              }}
            >
              <Box
                component="img"
                src={miniProgramQr}
                alt="中象在线微信小程序二维码"
                sx={{ width: 180, height: 180, borderRadius: 2, mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                微信小程序
              </Typography>
              <Typography variant="body2" color="text.secondary">
                微信扫码即可使用
              </Typography>
            </Card>

            {/* Android */}
            <Card
              sx={{
                textAlign: 'center',
                p: 4,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ color: '#3DDC84', mb: 2 }}>
                <AndroidIcon sx={{ fontSize: 64 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Android
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                支持 Android 7.0 及以上
              </Typography>
              {CONFIG.download.android && CONFIG.download.android !== '#' ? (
                <Button
                  variant="contained"
                  href={CONFIG.download.android}
                  sx={{
                    bgcolor: '#3DDC84',
                    color: '#000',
                    '&:hover': { bgcolor: '#3DDC84', filter: 'brightness(0.9)' },
                  }}
                >
                  下载 APK
                </Button>
              ) : (
                <Button variant="contained" disabled sx={{ '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
                  即将上线
                </Button>
              )}
            </Card>

            {/* iOS */}
            <Card
              sx={{
                textAlign: 'center',
                p: 4,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ color: '#000', mb: 2 }}>
                <AppleIcon sx={{ fontSize: 64 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                iOS
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                支持 iOS 13.0 及以上
              </Typography>
              <Button variant="contained" disabled sx={{ '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
                即将上线
              </Button>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* 功能特色 */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography variant="h2" align="center" sx={{ mb: 2 }}>
          一站式象棋平台
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}
        >
          无论你是象棋初学者还是资深棋手，中象在线都能为你提供全方位的学习与竞技体验
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 3,
          }}
        >
          {FEATURES.map((f) => (
            <Card
              key={f.title}
              variant="outlined"
              sx={{
                textAlign: 'center',
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  borderColor: f.color,
                },
              }}
            >
              <Box sx={{ color: f.color, mb: 2 }}>{f.icon}</Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {f.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {f.desc}
              </Typography>
            </Card>
          ))}
        </Box>
      </Container>


    </Box>
  );
}
